import React, { useState, useCallback, useRef, useEffect } from "react";
import { useHistory } from 'react-router-dom';
import { connect } from "react-redux";
import { Button } from 'devextreme-react/button';
import { TabPanel, Item as TabItem } from 'devextreme-react/tab-panel';
import { LoadPanel } from 'devextreme-react/load-panel';
import Toolbar, { Item } from "devextreme-react/toolbar";
import PrimaryTab from "./tabs/PrimaryTab";
import WeekDaysTab from "./tabs/WeekDaysTab";
import axios from "axios";
import data from "../../../../assets/apiRoutes.json";
import notify from 'devextreme/ui/notify';
import { confirm } from 'devextreme/ui/dialog';
import { CONCURRENCY_HANDLED, getApiErrorMessage, handleConcurrencyConflict } from "../../../../common/concurrency";

function Form(props) {
    const history = useHistory();
    const formRefs = useRef({});
    const initialFormFields = {
        ROWID: '00000000-0000-0000-0000-000000000000',
        RECORD: '',
        CODE: '',
        DESCRIPTION1: '',
        DESCRIPTION2: '',
        STATUS: 'A',
        COMMENT: '',
        // Opaque optimistic-concurrency token. Loaded with the record, echoed back on save, and
        // replaced with the token the save returns. Never displayed or parsed.
        CONCURRENCYTOKEN: null,
    };
    const [formFields, setformFields] = useState(initialFormFields);
    const [loadPanelVisible, setLoadPanelVisible] = useState(false);
    const accessToken = props?.oidc?.user?.access_token;

    const hideLoadPanel = useCallback(() => {
        setLoadPanelVisible(false);
    }, []);

    const position = { of: '#accordion' };

    const handleFieldChange = useCallback((name, value) => {
        setformFields(prevFields => ({
            ...prevFields,
            [name]: value
        }));
    }, []);

    const loadWeekSettingsHeaderById = useCallback(async (rowId) => {
        if (!rowId || rowId === '00000000-0000-0000-0000-000000000000') {
            return;
        }

        try {
            setLoadPanelVisible(true);
            const url = `${window._env_.REACT_APP_APIS}/${data.WeekSettingsHeader.GetWeekSettingsHeader}?rowid=${rowId}`;
            const response = await axios.get(url, {
                headers: {
                    access_token: accessToken,
                    Authorization: `Bearer ${accessToken}`
                }
            });

            if (response.data) {
                const item = response.data;
                setformFields({
                    ROWID: item.RowId || initialFormFields.ROWID,
                    RECORD: item.RecordNumber || '',
                    CODE: item.Code || '',
                    DESCRIPTION1: item.Description1 || '',
                    DESCRIPTION2: item.Description2 || '',
                    STATUS: item.Status || 'A',
                    COMMENT: item.Comment || '',
                    CONCURRENCYTOKEN: item.ConcurrencyToken || null,
                });
            }
        } catch (error) {
            console.error('Error loading Week Settings Header:', error);
            notify('Failed to load Warehouse Weekly Settings data.', 'error', 3000);
        } finally {
            setLoadPanelVisible(false);
        }
    }, [accessToken]);

    useEffect(() => {
        const rowId = props.id;
        if (rowId && rowId !== '00000000-0000-0000-0000-000000000000') {
            loadWeekSettingsHeaderById(rowId);
        }
    }, [props.id, loadWeekSettingsHeaderById]);

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setLoadPanelVisible(true);

        try {
            const payload = {
                RowId: formFields.ROWID && formFields.ROWID !== "00000000-0000-0000-0000-000000000000" ? formFields.ROWID : "00000000-0000-0000-0000-000000000000",
                KeySequence: 0,
                Code: formFields.CODE || "",
                Description1: formFields.DESCRIPTION1 || "",
                Description2: formFields.DESCRIPTION2 || "",
                Status: formFields.STATUS || "A",
                Comment: formFields.COMMENT || "",
                // Tells the API which version of the row this edit started from. Omitted on insert.
                ConcurrencyToken: formFields.CONCURRENCYTOKEN || null,
            };

            const isUpdate = formFields.ROWID && formFields.ROWID !== "00000000-0000-0000-0000-000000000000";
            const endpoint = isUpdate ? data.WeekSettingsHeader.UpdateWeekSettingsHeader : data.WeekSettingsHeader.InsertWeekSettingsHeader;
            const method = isUpdate ? 'put' : 'post';

            const url = `${window._env_.REACT_APP_APIS}/${endpoint}`;
            const resp = await axios[method](url, payload, {
                headers: {
                    access_token: accessToken,
                    Authorization: `Bearer ${accessToken}`,
                }
            });

            const saved = resp?.data;
            if (saved) {
                setformFields((prev) => ({
                    ...prev,
                    ROWID: saved.RowId || prev.ROWID,
                    RECORD: saved.RecordNumber || prev.RECORD,
                    CODE: saved.Code || prev.CODE,
                    DESCRIPTION1: saved.Description1 || prev.DESCRIPTION1,
                    DESCRIPTION2: saved.Description2 || prev.DESCRIPTION2,
                    STATUS: saved.Status || prev.STATUS,
                    COMMENT: saved.Comment || prev.COMMENT,
                    // Adopt the token for the version just written. Without this the next save
                    // would still echo the token the page was opened with and falsely conflict.
                    CONCURRENCYTOKEN: saved.ConcurrencyToken || null,
                }));
            }

            notify("Warehouse Weekly Settings saved successfully.", "success", 3000);
        } catch (err) {
            // A stale save is already handled globally by the axios interceptor, which raises the
            // "record changed - reload" dialog. Don't stack a red toast on top of it.
            if (err?.[CONCURRENCY_HANDLED]) {
                return;
            }

            notify(getApiErrorMessage(err, "Failed to save Warehouse Weekly Settings."), "error", 5000);
        } finally {
            hideLoadPanel();
        }
    };

    const DeleteClick = () => {
        if (!formFields.ROWID || formFields.ROWID === "00000000-0000-0000-0000-000000000000") {
            notify("No record selected for deletion.", "warning", 3000);
            return;
        }

        let result = confirm("<i>Are you sure you want to delete this Warehouse Weekly Settings?</i>", "Delete");
        result.then(async (dialogResult) => {
            if (dialogResult) {
                setLoadPanelVisible(true);
                try {
                    const url = `${window._env_.REACT_APP_APIS}/${data.WeekSettingsHeader.DeleteWeekSettingsHeader}`;
                    const response = await axios.post(url, formFields.ROWID, {
                        headers: {
                            access_token: accessToken,
                            Authorization: `Bearer ${accessToken}`,
                            'Content-Type': 'application/json'
                        }
                    });

                    // Accept either casing: the delete endpoint's flag may serialize as
                    // Success (PascalCase) or success (camelCase) depending on the resolver.
                    const deleteSucceeded = response.data && (response.data.Success ?? response.data.success);
                    if (deleteSucceeded) {
                        notify("Warehouse Weekly Settings deleted successfully.", "success", 3000);
                        setformFields(initialFormFields);
                        setTimeout(() => {
                            history.push("/wm/codes/week-settings");
                        }, 1000);
                    } else if (!handleConcurrencyConflict(response.data)) {
                        // The delete endpoint reports failures as HTTP 200 with Success=false, so the
                        // global 409 interceptor never sees them - check the body for a conflict here.
                        notify(response.data?.message || "Failed to delete Warehouse Weekly Settings.", "error", 5000);
                    }
                } catch (error) {
                    if (error?.[CONCURRENCY_HANDLED]) {
                        return;
                    }

                    notify(getApiErrorMessage(error, "Failed to delete Warehouse Weekly Settings."), "error", 5000);
                } finally {
                    hideLoadPanel();
                }
            }
        });
    };

    const addNewRecordClick = () => {
        setformFields(initialFormFields);
    };

    const handleReset = () => {
        const rowId = props.id;
        if (rowId && rowId !== '00000000-0000-0000-0000-000000000000') {
            loadWeekSettingsHeaderById(rowId);
        } else {
            setformFields(initialFormFields);
        }
    };

    const goBack = () => {
        history.push("/wm/codes/week-settings");
    };

    return (
        <div>
            <div className="row dataEntryMainPage">
                <div className="col-md-12 main-page">
                    <div id="accordion">
                        <LoadPanel
                            shadingColor="rgba(0,0,0,0.4)"
                            position={position}
                            onHiding={hideLoadPanel}
                            visible={loadPanelVisible}
                            showIndicator={true}
                            shading={true}
                            showPane={true}
                            hideOnOutsideClick={false}
                        />
                        <form onSubmit={(e) => handleFormSubmit(e)}>
                            <Toolbar className="header">
                                <Item location="before">
                                    <div className="title">
                                        <h6
                                            style={{
                                                color: window._env_.Color2,
                                                marginLeft: '12px'
                                            }}
                                        >
                                            {formFields.CODE ? `Warehouse Weekly Settings - ${formFields.CODE}` : 'Warehouse Weekly Settings'}
                                        </h6>
                                    </div>
                                </Item>
                                <Item location="after">
                                    <Button
                                        text="Save"
                                        useSubmitBehavior={true}
                                        type="default"
                                        className="accordianButton"
                                    />
                                </Item>
                                <Item location="after">
                                    <Button className="accordianButton" onClick={addNewRecordClick} text="New" type="default" />
                                </Item>
                                <Item location="after">
                                    <Button className="accordianButton" onClick={DeleteClick} text="Delete" disabled={formFields.ROWID === "00000000-0000-0000-0000-000000000000"} type="default" />
                                </Item>
                                <Item location="after">
                                    <Button className="accordianButton" onClick={handleReset} text="Reset" />
                                </Item>
                                <Item location="after">
                                    <Button className="accordianButton" onClick={goBack} text="Back" />
                                </Item>
                            </Toolbar>
                            <TabPanel deferRendering={props.id ? true : false} showNavButtons={true} height={"auto"}>
                                <TabItem title="Primary">
                                    <PrimaryTab
                                        formFields={formFields}
                                        setformFields={setformFields}
                                        formRefs={formRefs}
                                        handleFieldChange={handleFieldChange}
                                    />
                                </TabItem>
                                <TabItem title="Week Days">
                                    <WeekDaysTab
                                        headerId={formFields.ROWID}
                                    />
                                </TabItem>
                            </TabPanel>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}

function mapStateToProps(state) {
    return {
        oidc: state.oidc
    };
}

export default connect(mapStateToProps)(Form);
